// The library (TAGS.md §3b, §4): articles synced from the user's database plus links added here; sort Last opened
// (default) / Date added; filter by tags (Match all / Match any); add a link (server fetch); edit tags; delete.
import { account } from "../api/account";
import { levelsStore } from "../levels/levelsStore";
import { addedAt, displaySource, title, type Article, wordCount } from "../model/article";
import { addLink } from "../model/addLink";
import { library } from "../model/library";
import { set, settings } from "../settings";
import { sync } from "../sync/sync";
import { counts, edit, effective, matches, rowTopics, subcategories } from "../tags/tags";
import { taxonomy } from "../tags/taxonomyStore";
import { clear, h, icon, plural } from "./dom";

export interface LibraryHost { open(id: string): void; settings(): void }

interface FilterState { selected: string[]; mode: "all" | "any" }
const filter: FilterState = (() => { try { return JSON.parse(sessionStorage.getItem("depth.filter") || "") as FilterState; } catch { return { selected: [], mode: "all" }; } })();
const saveFilter = () => { try { sessionStorage.setItem("depth.filter", JSON.stringify(filter)); } catch { /* ignore */ } };

function lastOpened(a: Article): number {
  return Math.max(a.openedAt ?? 0, levelsStore.position(a.id)?.updatedAt ?? 0, a.progressUpdatedAt, addedAt(a));
}

export class LibraryView {
  readonly root: HTMLDivElement;
  private list: HTMLDivElement;
  private chips: HTMLDivElement;
  private status: HTMLDivElement;
  private unsubs: (() => void)[] = [];
  private search = "";

  constructor(private host: LibraryHost) {
    this.list = h("div.lib-list");
    this.chips = h("div.chips");
    this.status = h("div.sync-status");
    const searchInput = h("input.search", { type: "search", placeholder: "Search titles", "aria-label": "Search titles",
      oninput: (e: Event) => { this.search = (e.target as HTMLInputElement).value.toLowerCase(); this.render(); } });
    this.root = h("div.library", null,
      h("header.lib-head", null,
        h("div.brand", null, h("img", { src: "/icon.png", alt: "" }), "Depth"),
        h("div.lib-actions", null,
          h("button.icon-btn", { title: "Sync now", "aria-label": "Sync now", onclick: () => sync.syncNow() }, icon("sync")),
          h("button.icon-btn", { title: "Sort", "aria-label": "Sort", onclick: (e: Event) => this.sortMenu(e.currentTarget as HTMLElement) }, icon("sort")),
          h("button.icon-btn", { title: "Filter by tags", "aria-label": "Filter by tags", onclick: () => this.filterSheet() }, icon("filter")),
          h("button.icon-btn", { title: "Settings", "aria-label": "Settings", onclick: () => this.host.settings() }, icon("gear")))),
      h("form.add-link", { onsubmit: (e: Event) => { e.preventDefault(); this.add(e.target as HTMLFormElement); } },
        h("input", { name: "url", type: "url", inputmode: "url", placeholder: "Paste a link to add an article", "aria-label": "Link to add", autocomplete: "off" }),
        h("button.btn.primary", { type: "submit" }, icon("plus", 18), "Add")),
      h("div.lib-tools", null, searchInput, this.chips),
      this.status,
      this.list);
  }

  mount(parent: HTMLElement) {
    parent.appendChild(this.root);
    this.unsubs.push(library.events.on("changed", () => this.render()), sync.events.on("status", () => this.renderStatus()),
      taxonomy.events.on("changed", () => this.render()), account.events.on("changed", () => this.render()));
    this.render();
    this.renderStatus();
  }

  destroy() { for (const u of this.unsubs) u(); this.root.remove(); }

  private async add(form: HTMLFormElement) {
    const input = form.elements.namedItem("url") as HTMLInputElement;
    const url = input.value;
    if (!url.trim()) return;
    input.value = "";
    try { await addLink(url); } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  }

  private renderStatus() {
    const s = this.status;
    clear(s);
    if (!account.signedIn) return;
    if (!account.features.sync) s.append("Sync isn't available for this account; your library stays in this browser.");
    else if (sync.syncing) s.append("Syncing…");
    else if (sync.lastError) s.append(h("span.error", null, sync.lastError));
  }

  private render() {
    const t = taxonomy.list;
    const all = library.list().filter((a) => !a.isBook);
    const ready = all.filter((a) => a.status === "ready");
    const fetching = all.filter((a) => a.status !== "ready").sort((a, b) => addedAt(b) - addedAt(a));
    const tagsOf = (a: Article) => effective(a.meta.tags, t);
    const order = (a: Article) => (settings.librarySort === "added" ? addedAt(a) : lastOpened(a));
    const shown = ready.filter((a) => matches(tagsOf(a), filter.selected, filter.mode, t)
      && (!this.search || title(a).toLowerCase().includes(this.search))).sort((a, b) => order(b) - order(a));
    const upNext = shown.filter((a) => !a.progress.finished), finished = shown.filter((a) => a.progress.finished);

    clear(this.chips);
    if (filter.selected.length) {
      const names = new Map<string, string>([...t.categories.map((c) => ["cat:" + c.id, c.name] as [string, string]), ...subcategories(t).map(([, s]) => [s.id, s.name] as [string, string])]);
      for (const id of filter.selected) {
        this.chips.append(h("button.chip", { onclick: () => { filter.selected = filter.selected.filter((x) => x !== id); saveFilter(); this.render(); } }, names.get(id) ?? id, icon("close", 14)));
      }
      if (filter.selected.length > 1) this.chips.append(h("button.chip.mode", { onclick: () => { filter.mode = filter.mode === "all" ? "any" : "all"; saveFilter(); this.render(); } }, filter.mode === "all" ? "Match all" : "Match any"));
    }

    clear(this.list);
    if (!account.signedIn) return;
    if (fetching.length) this.list.append(h("h2.section", null, "Fetching"), ...fetching.map((a) => this.row(a)));
    if (upNext.length) this.list.append(h("h2.section", null, settings.librarySort === "added" ? "Up next · newest added" : "Up next"), ...upNext.map((a) => this.row(a)));
    if (finished.length) this.list.append(h("h2.section", null, "Finished"), ...finished.map((a) => this.row(a)));
    if (!shown.length && !fetching.length) {
      this.list.append(h("div.empty", null, ready.length ? "Nothing matches this filter." : sync.syncing ? "Loading your library…" : "Your library is empty. Paste a link above to add an article."));
    }
  }

  private row(a: Article): HTMLElement {
    const t = taxonomy.list;
    const topics = rowTopics(a.meta.tags, t);
    const img = a.meta.images?.[0];
    const progress = a.meta.chunks?.length ? Math.min(1, (a.progress.chunk + (a.progress.offset > 0 ? 0.5 : 0)) / a.meta.chunks.length) : 0;
    const meta = a.status === "fetching" ? "Fetching on your server…"
      : a.status === "failed" ? a.error ?? "Couldn't fetch it."
      : [displaySource(a), `${Math.max(1, Math.round(wordCount(a) / 230))} min read`].join(" · ");
    const row = h("div.row", { role: a.status === "ready" ? "button" : undefined, tabindex: a.status === "ready" ? "0" : undefined, dataset: { id: a.id } },
      h("div.row-main", null,
        h("div.row-title", null, a.status === "ready" ? title(a) : a.url ?? title(a)),
        h("div.row-meta", { class: a.status === "failed" ? "row-meta error" : "row-meta" }, meta),
        topics.length ? h("div.row-tags", null, topics.join(" · ")) : null,
        progress > 0 && !a.progress.finished ? h("div.row-progress", null, h("i", { style: { width: `${progress * 100}%` } })) : null),
      img ? h("img.thumb", { src: img.src, alt: "", loading: "lazy", referrerpolicy: "no-referrer-when-downgrade", onerror: (e: Event) => (e.target as HTMLElement).remove() }) : null,
      h("button.icon-btn.small.more", { "aria-label": "More", title: "More", onclick: (e: Event) => { e.stopPropagation(); this.rowMenu(a, e.currentTarget as HTMLElement); } }, "⋯"));
    if (a.status === "ready") {
      row.addEventListener("click", () => this.host.open(a.id));
      row.addEventListener("keydown", (e) => { if (e.key === "Enter") this.host.open(a.id); });
    }
    return row;
  }

  private rowMenu(a: Article, anchor: HTMLElement) {
    const items: [string, () => void][] = [];
    if (a.status === "ready") items.push(["Edit tags…", () => this.editTags(a.id)]);
    if (a.status === "ready" && a.meta.sourceURL) items.push(["Open original", () => window.open(a.meta.sourceURL!, "_blank", "noopener")]);
    if (a.status === "ready") items.push([a.progress.finished ? "Mark as not finished" : "Mark as finished", () => library.setProgress(a.id, a.progress.finished ? 0 : a.progress.chunk, 0, !a.progress.finished)]);
    if (a.status === "failed" && a.url) items.push(["Retry", () => addLink(a.url!, a.id).catch(() => {})]);
    items.push(["Delete", () => { if (confirm(`Delete “${title(a)}”? It's removed from every device.`)) a.status === "ready" ? library.delete(a.id) : library.removeLocal(a.id); }]);
    popMenu(anchor, items);
  }

  private sortMenu(anchor: HTMLElement) {
    popMenu(anchor, [["Last opened", "lastOpened"], ["Date added", "added"]].map(([label, v]) =>
      [(settings.librarySort === v ? "✓ " : "") + label, () => { set("librarySort", v as "lastOpened" | "added"); this.render(); }]));
  }

  private filterSheet() {
    const t = taxonomy.list;
    const c = counts(library.list().filter((a) => a.status === "ready").map((a) => effective(a.meta.tags, t)), t);
    const toggle = (id: string) => {
      filter.selected = filter.selected.includes(id) ? filter.selected.filter((x) => x !== id) : [...filter.selected, id];
      saveFilter(); this.render(); draw();
    };
    const body = h("div.sheet-body");
    const draw = () => {
      clear(body);
      body.append(h("div.segmented.wide", null, ...(["all", "any"] as const).map((m) => h("button", { class: filter.mode === m ? "on" : "",
        onclick: () => { filter.mode = m; saveFilter(); this.render(); draw(); } }, m === "all" ? "Match all" : "Match any"))));
      for (const cat of t.categories) {
        if (!c["cat:" + cat.id]) continue;
        body.append(h("button.filter-cat", { class: filter.selected.includes("cat:" + cat.id) ? "filter-cat on" : "filter-cat", onclick: () => toggle("cat:" + cat.id) },
          cat.name, h("span.count", null, String(c["cat:" + cat.id]))));
        for (const s of cat.subcategories) {
          if (!c[s.id]) continue;
          body.append(h("button.filter-sub", { class: filter.selected.includes(s.id) ? "filter-sub on" : "filter-sub", onclick: () => toggle(s.id) },
            filter.selected.includes(s.id) ? icon("check", 16) : h("span.check-space"), s.name, h("span.count", null, String(c[s.id]))));
        }
      }
      if (!Object.keys(c).length) body.append(h("p.muted", null, "No tags yet. Articles are tagged when they're added."));
    };
    draw();
    sheet("Filter", body, filter.selected.length ? [["Clear", () => { filter.selected = []; saveFilter(); this.render(); draw(); }]] : []);
  }

  private editTags(id: string) {
    const body = h("div.sheet-body");
    const draw = () => {
      const a = library.get(id);
      if (!a) return;
      const t = taxonomy.list;
      const have = new Set(effective(a.meta.tags, t));
      const auto = new Set(a.meta.tags?.auto ?? []);
      clear(body);
      for (const cat of t.categories) {
        body.append(h("div.filter-cat.static", null, cat.name));
        for (const s of cat.subcategories) {
          body.append(h("button.filter-sub", { class: have.has(s.id) ? "filter-sub on" : "filter-sub",
            onclick: () => { library.editMeta(id, (m) => { m.tags = edit(m.tags, s.id, !have.has(s.id)); }); draw(); } },
            have.has(s.id) ? icon("check", 16) : h("span.check-space"), s.name, auto.has(s.id) ? h("span.sparkle", { title: "From tagging" }, "✦") : null));
        }
      }
    };
    draw();
    sheet("Edit tags", body, []);
  }
}

export function popMenu(anchor: HTMLElement, items: [string, () => void][]) {
  document.querySelector(".menu.floating")?.remove();
  const r = anchor.getBoundingClientRect();
  const menu = h("div.menu.floating", { style: { top: `${r.bottom + 4}px`, right: `${Math.max(8, window.innerWidth - r.right)}px` } },
    ...items.map(([label, fn]) => h("button", { onclick: () => { close(); fn(); } }, label)));
  const close = () => { menu.remove(); document.removeEventListener("pointerdown", outside, true); };
  const outside = (e: Event) => { if (!menu.contains(e.target as Node)) close(); };
  document.body.appendChild(menu);
  setTimeout(() => document.addEventListener("pointerdown", outside, true));
}

export function sheet(titleText: string, body: HTMLElement, actions: [string, () => void][], onClose?: () => void): () => void {
  const close = () => { back.remove(); document.removeEventListener("keydown", esc, true); onClose?.(); };
  const esc = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); close(); } };
  const back = h("div.sheet-backdrop", { onclick: (e: Event) => { if (e.target === back) close(); } },
    h("div.sheet", { role: "dialog", "aria-label": titleText },
      h("div.sheet-head", null, h("h2", null, titleText), ...actions.map(([l, fn]) => h("button.link", { onclick: fn }, l)),
        h("button.icon-btn.small", { "aria-label": "Close", onclick: close }, icon("close", 18))), body));
  document.addEventListener("keydown", esc, true);
  document.body.appendChild(back);
  return close;
}

export { plural };
